import { BaseProps } from '../types';
import { Stack, SxProps, Typography } from '@mui/material';
import React, { FC, useMemo } from 'react';
import { z } from 'zod';

const stringSchema = z.string();

export interface Props extends BaseProps {
  /**
   * Optional. String or JSX component that will be the Label of the component
   */
  label?: string | React.ReactNode;
  /**
   * Optional. Icon that will be the main center piece of the ZeroState component
   */
  icon?: React.ReactNode;
  /**
   * Optional. Typically button actions at the bottom of the component
   */
  actions?: React.ReactNode;

  /**
   * Allows tweaking of the root component
   */
  sx?: SxProps;
}

const useSxProps = () => {
  return useMemo(() => {
    return {
      root: {
        borderRadius: '12px',
        padding: '12px',
      },
    };
  }, []);
};

export const ZeroState: FC<Props> = ({ dataTestId, label, icon, actions, sx }) => {
  const sxProps = useSxProps();
  const parsedLabel = stringSchema.safeParse(label);

  if (!label && !icon && !actions) {
    return null;
  }

  return (
    <div data-testid={dataTestId}>
      <Stack
        // eslint-disable-next-line no-restricted-syntax
        sx={{
          ...sxProps.root,
          ...sx,
        }}
        direction="column"
        alignItems="center"
        justifyContent="center"
        spacing={2}
      >
        {icon}
        {parsedLabel.success && (
          // A parsed text label uses the standard centered empty-state style.
          // eslint-disable-next-line no-restricted-syntax
          <Typography sx={{ textAlign: 'center' }} variant="body1" fontWeight={400} color="text.secondary">
            {parsedLabel.data}
          </Typography>
        )}
        {!parsedLabel.success && label}
        {actions}
      </Stack>
    </div>
  );
};
